import type { Item, LinkedItem } from '@types'
import type CameraControls from 'camera-controls'
import type { PerspectiveCamera, Scene, WebGLRenderer } from 'three'

import { applyDrawCameraFit } from './applyDrawCameraFit'
import { captureCameraViewFromControls, type CameraView } from './cameraView'

export type CameraCaptureContext = {
  controls: CameraControls
  camera: PerspectiveCamera
  gl: WebGLRenderer
  scene: Scene
}

let captureContext: CameraCaptureContext | null = null
let activeDrawFitRetryCancel: (() => void) | null = null

/** Retrasos cortos: solo cubren el primer frame tras dibujar, sin pelear con la órbita manual. */
const DRAW_FIT_RETRY_DELAYS_MS = [0, 50]

export function registerCameraCaptureContext(ctx: CameraCaptureContext | null): void {
  captureContext = ctx
}

export function getCameraCaptureContext(): CameraCaptureContext | null {
  return captureContext
}

/** Lee posición/target actuales de los controles 3D (si el viewer está montado). */
export function captureLiveCameraView(): CameraView | null {
  if (!captureContext) return null
  const { controls, camera } = captureContext
  return captureCameraViewFromControls(controls, camera)
}

/** Encuadre inmediato tras dibujar (sin esperar al efecto de CameraManager3D). */
export function applyLiveDrawCameraFit(items: Map<string, Item | LinkedItem>): CameraView | null {
  if (!captureContext) return null
  const { controls, camera } = captureContext
  return applyDrawCameraFit(controls, camera, items)
}

export function cancelLiveDrawCameraFitRetries(): void {
  activeDrawFitRetryCancel?.()
  activeDrawFitRetryCancel = null
}

/** Reaplica el encuadre cenital completo (incluye `camera.up`) en ticks breves. */
export function scheduleLiveDrawCameraFitRetries(
  items: Map<string, Item | LinkedItem>,
): () => void {
  if (!captureContext) return () => {}

  cancelLiveDrawCameraFitRetries()

  const timers = DRAW_FIT_RETRY_DELAYS_MS.map((delay) =>
    window.setTimeout(() => {
      applyLiveDrawCameraFit(items)
    }, delay),
  )

  activeDrawFitRetryCancel = () => {
    timers.forEach((id) => window.clearTimeout(id))
  }

  return () => {
    cancelLiveDrawCameraFitRetries()
  }
}
